/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component, Fragment } from "react";
import { rhpDropinIds, rhpDropinIdsForCommenting } from "constants/enums";
import * as stores from "stores";
import { NavigationBarContainer } from "containers/navigationBar/NavigationBarContainer";
import { supportedFileExtensions } from "constants/enums";
import RHPWrapperCss from "./RHPWrapper.css";
import { RHP_OPEN_STATE_EVENT } from "../../constants/constants";
import { getViewerAnalyticsForKey } from "../../utils/DocumentUtils";

class RHPWrapper extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isRHPLoaded: false
    };
    this._init();
    this._setRHPRef = this._setRHPRef.bind(this);
  }

  _init() {
    this.RHPComponent = null;
    this._loadRHPComponent();
  }

  _shouldLoadComponent() {
    return (
      this.props.showRhp &&
      Object.values(supportedFileExtensions).includes(this.props.fileExtension) &&
      !this.state.isRHPLoaded &&
      this.props.shouldUseModernViewer
    );
  }

  componentDidUpdate() {
    if (this._shouldLoadComponent()) {
      this._loadRHPComponent();
    }
  }

  async _loadRHPComponent() {
    if (this._shouldLoadComponent()) {
      this.RHPComponent = await this.props.getRHPComponent();
      this._setRHP();
    }
  }

  _setRHP() {
    this.setState({
      isRHPLoaded: true
    });
  }

  _setRHPRef(elementRef) {
    if (elementRef) {
      elementRef.getPublicApis().then(apis => apis.beforeOpenPanel(this.props.beforeUpdatingActiveDropin));
      this.RHPRef = elementRef;
      stores.appStore.setRHPRef(elementRef);
      // Subscribe to RHP to get currRHPFeature
      this.RHPRef.apiService.registeredPublicApis.subscribe(this._setSelectedDropin);
      const { dropinId: currentActiveDropin } = this.RHPRef.apiService.registeredPublicApis.getConfig();
      this._setSelectedDropin({ currentActiveDropin });
      this.RHPRef.apiService.registeredPublicApis.customEventsSubscribe(this._setResizingRHPStatus, "resizeRHP");
      this.RHPRef.apiService.registeredPublicApis.customEventsSubscribe(
        stores.skinnyRailStore.setRhpOpenState,
        RHP_OPEN_STATE_EVENT
      );
    }
  }

  _setResizingRHPStatus({ isRHPResizing }) {
    stores.appStore.setIsResizing(isRHPResizing);
  }

  _setSelectedDropin(dropinObj) {
    stores.appStore.setCurrRHPFeature(dropinObj.currentActiveDropin);
  }

  _getRHPProps(props) {
    const selectedDropin = this.props.selectedDropin;
    const { shouldRenderPersonalCommentsDropin, shouldRenderCommentsDropin, rhpSkeletonDropinId } = props;
    return {
      skinnyRailRef: this._setRHPRef,
      dropinIds: this.props.enabledVerbs,
      topUIControls: this.props.topUIControls ? [...this.props.topUIControls] : [],
      bottomUIControls: this.props.bottomUIControls ? [...this.props.bottomUIControls] : [],
      selectedDropin,
      fileExtension: this.props.fileExtension,
      connectorAsset: this.props.connectorAsset,
      intl: props.intl,
      footer: NavigationBarContainer,
      previewConfig: {
        shouldRenderCommentsDropin,
        shouldRenderPersonalCommentsDropin,
        rhpSkeletonDropinId
      },
      panelWidth: props.panelWidth,
      enableRHPResizing: props.enableRHPResizing,
      getViewerAnalyticsForKey
    };
  }

  render() {
    const shouldRenderComponent = this.props.showRhp && this.state.isRHPLoaded && this.props.shouldUseModernViewer;
    const props = this._getRHPProps(this.props);
    return (
      <Fragment>
        {shouldRenderComponent ? (
          <this.RHPComponent {...props} />
        ) : this.props.showRhp && this.props.shouldUseModernViewer ? (
          <div className={RHPWrapperCss.RHPLoader} />
        ) : null}
      </Fragment>
    );
  }
}

export { RHPWrapper };
